import React from "react";
import { getAsset } from "../../data/imageAssets";

/**
 * OptimizedImage — Bild-Wrapper nach Spec aus SAT Bildkollektion:
 * - Aspect-Ratio Container (verhindert Layout-Shift)
 * - Fokuspunkt (object-position) für sichere Ausschnitte
 * - Loading-Priorität (LCP: eager · Rest: lazy)
 * - Mobile-Variante bei kleineren Breakpoints via <picture>
 *
 * Nutzung:
 *   <OptimizedImage assetId="FRA-01" priority ratio="16/9" />
 */
const OptimizedImage = ({
  assetId,
  ratio = "16/9",       // z.B. "16/9", "4/5", "3/2", "1/1"
  ratioMobile,           // optional zweite Ratio unter md-Breakpoint
  priority = false,      // LCP-Bild? -> eager laden
  className = "",
  overlayClassName = "", // z.B. Text-Safe-Overlay
  children,              // Inhalt (Text/CTA über dem Bild)
}) => {
  const asset = getAsset(assetId);
  if (!asset) {
    // Sanftes Fallback: leerer Farbverlauf, wenn kein Asset zugewiesen
    return (
      <div
        className={`w-full bg-gradient-to-br from-white/[0.04] to-white/[0.01] rounded-2xl ${className}`}
        style={{ aspectRatio: ratio }}
      />
    );
  }

  const focal = asset.focal || { x: 0.5, y: 0.5 };
  const posStyle = { objectPosition: `${(focal.x * 100).toFixed(0)}% ${(focal.y * 100).toFixed(0)}%` };

  const desktop = asset.src;
  const mobile = asset.src_mobile || asset.src;

  return (
    <div
      className={`relative overflow-hidden w-full ${className}`}
      style={{
        aspectRatio: ratio,
        // md+ = desktop ratio, unter md = ratioMobile falls angegeben
        ...(ratioMobile ? { ["--ratio-mobile"]: ratioMobile } : {}),
      }}
      data-testid={`img-${assetId}`}
    >
      <picture>
        {ratioMobile && (
          <source media="(max-width: 767px)" srcSet={mobile} />
        )}
        <img
          src={desktop}
          alt={asset.alt || ""}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding={priority ? "sync" : "async"}
          className="absolute inset-0 w-full h-full object-cover"
          style={posStyle}
          width="1920"
          height="1080"
        />
      </picture>

      {/* Overlay-Slot (z.B. für Text-Safe-Area / Verlauf) */}
      {overlayClassName && <div className={`absolute inset-0 ${overlayClassName}`} />}

      {/* Interner Content (Text/CTA über dem Bild) */}
      {children && <div className="relative z-10 w-full h-full">{children}</div>}

      {/* DEV-Marker für Platzhalter — später entfernen wenn approved=true */}
      {asset.placeholder && (
        <span
          className="absolute bottom-1.5 right-2 text-[9px] tracking-widest uppercase text-white/50 bg-black/40 px-1.5 py-0.5 rounded"
          title={`${asset.id} · Placeholder — Editorial-Produktion ausstehend`}
        >
          {asset.id}
        </span>
      )}
    </div>
  );
};

export default OptimizedImage;
